import { Checkbox, Code, Flex, Hint, Spacer } from '@lokalise/louis';

import Bracket from '@app/components/Bracket';

import type { ProjectType } from '../../../../types/project';
import type { PlaceholderFormat, PluralFormat } from '../../types';
import { FileFormat } from '../../types';
import ExportAccordion from '../ExportAccordion';
import ExportEmptyInput from './components/ExportEmptyInput';
import ExportNullAsInput from './components/ExportNullAsInput';
import ExportSortInput from './components/ExportSortInput';
import IndentationInput from './components/IndentationInput';
import JavaPropertiesEncodingInput from './components/JavaPropertiesEncodingInput';
import JavaPropertiesSeparatorInput from './components/JavaPropertiesSeparatorInput';
import OptionsCheckboxes from './components/OptionsCheckboxes';
import PlaceholderFormatInput from './components/PlaceholderFormatInput';
import PluralFormatInput from './components/PluralFormatInput';
import { InputGrid } from './styles';
import type { Value } from './types';
import {
    isExportAsNullVisible,
    isExportEmptyInputVisible,
    isExportSortInputVisible,
    isJavaPropertiesEncodingInputVisible,
    isJavaPropertiesSeparatorInputVisible,
    isOptionsVisible,
    isPlaceholderFormatInputVisible,
    isPluralFormatInputVisible,
    isSwitchEscapePercentInputVisible,
    isSwitchIcuNumericInputVisible,
} from './utils/visibilityHelpers';

interface OptionsProps {
    value: Value;
    fileFormat: FileFormat;
    projectType: ProjectType;
    isNoLanguageFoldersSwitchDisabled: boolean;
    pluralFormatOptions: { value: PluralFormat; label: string }[];
    placeholderFormatOptions: { value: PlaceholderFormat; label: string }[];
    projectId: string;
    onChange: (value: Value) => void;
}

const Options = ({
    value,
    fileFormat,
    projectType,
    isNoLanguageFoldersSwitchDisabled,
    pluralFormatOptions,
    placeholderFormatOptions,
    projectId,
    onChange,
}: OptionsProps) => (
    <ExportAccordion heading={<b>Advanced settings</b>} name="advanced-settings" projectId={projectId}>
        <Spacer top={4}>
            <Flex gap={7} direction="column">
                {isOptionsVisible(projectType) && (
                    <OptionsCheckboxes
                        value={{
                            switchCompact: value.switchCompact,
                            switchIncludeDescription: value.switchIncludeDescription,
                            switchIncludeComments: value.switchIncludeComments,
                            switchNoLanguageFolders: value.switchNoLanguageFolders,
                            switchDisableReferences: value.switchDisableReferences,
                            switchYamlIncludeRoot: value.switchYamlIncludeRoot,
                            jsonUnescapedSlashes: value.jsonUnescapedSlashes,
                            addNewlineEof: value.addNewlineEof,
                            replaceLineBreaks: value.replaceLineBreaks,
                        }}
                        fileFormat={fileFormat}
                        isNoLanguageFoldersSwitchDisabled={isNoLanguageFoldersSwitchDisabled}
                        onChange={(val) =>
                            onChange({
                                ...value,
                                ...val,
                            })
                        }
                    />
                )}
                <InputGrid>
                    {isExportEmptyInputVisible(fileFormat) && (
                        <ExportEmptyInput
                            value={value.exportEmpty}
                            fileFormat={fileFormat}
                            onChange={(exportEmpty) =>
                                onChange({
                                    ...value,
                                    exportEmpty,
                                })
                            }
                        />
                    )}

                    {isExportAsNullVisible(fileFormat) && (
                        <ExportNullAsInput
                            value={value.exportNullAs}
                            onChange={(exportNullAs) =>
                                onChange({
                                    ...value,
                                    exportNullAs,
                                })
                            }
                        />
                    )}

                    {isExportSortInputVisible(fileFormat, projectType) && (
                        <ExportSortInput
                            value={value.exportSort}
                            onChange={(exportSort) =>
                                onChange({
                                    ...value,
                                    exportSort,
                                })
                            }
                        />
                    )}
                    {isJavaPropertiesEncodingInputVisible(fileFormat) && (
                        <JavaPropertiesEncodingInput
                            value={value.javaPropertiesEncoding}
                            onChange={(javaPropertiesEncoding) =>
                                onChange({
                                    ...value,
                                    javaPropertiesEncoding,
                                })
                            }
                        />
                    )}
                    {isJavaPropertiesSeparatorInputVisible(fileFormat) && (
                        <JavaPropertiesSeparatorInput
                            value={value.javaPropertiesSeparator}
                            onChange={(javaPropertiesSeparator) =>
                                onChange({
                                    ...value,
                                    javaPropertiesSeparator,
                                })
                            }
                        />
                    )}
                    {fileFormat !== FileFormat.androidSdk &&
                        pluralFormatOptions.length > 0 &&
                        value.pluralFormat !== undefined && (
                            <Flex gap={2} direction="column">
                                {isPluralFormatInputVisible(fileFormat) ? (
                                    <>
                                        <PluralFormatInput
                                            value={value.pluralFormat}
                                            options={pluralFormatOptions}
                                            onChange={(pluralFormat) =>
                                                onChange({
                                                    ...value,
                                                    pluralFormat,
                                                })
                                            }
                                        />

                                        {isSwitchIcuNumericInputVisible(value.pluralFormat) && (
                                            <Bracket>
                                                <Checkbox
                                                    checked={value.switchIcuNumeric}
                                                    onChange={() =>
                                                        onChange({
                                                            ...value,
                                                            switchIcuNumeric: !value.switchIcuNumeric,
                                                        })
                                                    }
                                                >
                                                    Numeric plural forms{' '}
                                                    <Hint placement="top">
                                                        When enabled, plural forms <Code>zero</Code>, <Code>one</Code>{' '}
                                                        and <Code>two</Code> will be replaced with <Code>=0</Code>,{' '}
                                                        <Code>=1</Code> and
                                                        <Code>=2</Code> respectively.
                                                    </Hint>
                                                </Checkbox>
                                            </Bracket>
                                        )}
                                    </>
                                ) : null}
                            </Flex>
                        )}
                    {isPlaceholderFormatInputVisible(fileFormat) &&
                        placeholderFormatOptions.length > 0 &&
                        value.placeholderFormat !== undefined && (
                            <Flex gap={2} direction="column">
                                <PlaceholderFormatInput
                                    value={value.placeholderFormat}
                                    options={placeholderFormatOptions}
                                    onChange={(placeholderFormat) =>
                                        onChange({
                                            ...value,
                                            placeholderFormat,
                                        })
                                    }
                                />
                                {isSwitchEscapePercentInputVisible(value.placeholderFormat) && (
                                    <Bracket>
                                        <Checkbox
                                            checked={value.switchEscapePercent}
                                            onChange={() =>
                                                onChange({
                                                    ...value,
                                                    switchEscapePercent: !value.switchEscapePercent,
                                                })
                                            }
                                        >
                                            Convert all <Code>[%]</Code> to <Code>%%</Code>{' '}
                                            <Hint placement="top">
                                                When enabled, all universal percent placeholders <Code>[%]</Code> will
                                                be always exported as <Code>%%</Code>.
                                            </Hint>
                                        </Checkbox>
                                    </Bracket>
                                )}
                            </Flex>
                        )}
                    {value.indentation !== undefined && value.indentation !== 0 && (
                        <IndentationInput
                            value={value.indentation}
                            fileFormat={fileFormat}
                            onChange={(indentation) =>
                                onChange({
                                    ...value,
                                    indentation,
                                })
                            }
                        />
                    )}
                </InputGrid>
            </Flex>
        </Spacer>
    </ExportAccordion>
);

export default Options;
